import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ThemeCtx = createContext(null);

const osPrefersLight = () =>
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: light)").matches
    : false;

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const stored = typeof window !== "undefined" ? localStorage.getItem("glh_theme") : null;

  // First visit (no stored choice) follows the OS preference.
  const [theme, setTheme] = useState(stored || (osPrefersLight() ? "light" : "dark"));
  const [explicit, setExplicit] = useState(!!stored);

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
  }, [theme]);

  // Keep following the OS until the user (or their saved profile) makes a choice.
  useEffect(() => {
    if (explicit || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = (e) => setTheme(e.matches ? "light" : "dark");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [explicit]);

  // On sign-in, adopt the theme saved on the user's server profile (cross-device).
  useEffect(() => {
    if (user && (user.theme === "light" || user.theme === "dark")) {
      setTheme(user.theme);
      setExplicit(true);
      localStorage.setItem("glh_theme", user.theme);
    }
  }, [user]);

  const apply = (t) => {
    setTheme(t);
    setExplicit(true);
    localStorage.setItem("glh_theme", t);
    if (user) api.put("/auth/preferences", { theme: t }).catch(() => {});
  };

  const toggle = () => apply(theme === "light" ? "dark" : "light");

  return <ThemeCtx.Provider value={{ theme, toggle }}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
